import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import io from 'socket.io-client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import PlayerPickerSnake from './playerpicker/PlayerPickerSnake';
import DraftStatusHeader from './draftstatus/DraftStatusHeader';
import DraftBoard from './draftboard/DraftBoard';
import EntrantRosters from './entrantrosters/EntrantRosters';
import './SnakeDraftModule.css';
import { toast } from 'react-hot-toast';
import DraftOrderModal from '../modals/DraftOrderModal';
// We may need a different Lineup component or modify LineupSnake later.
// For now, we will focus on the main draft experience.

// In production, we need to connect to the WebSocket server at /socket.io
const SOCKET_URL = process.env.NODE_ENV === 'production'
  ? `${process.env.REACT_APP_PRODUCTION_API_BASE_URL}`
  : 'http://localhost:5000';

const SnakeDraftModule = ({ contest }) => {
  const [socket, setSocket] = useState(null);
  const [draftState, setDraftState] = useState(null);
  const [stagedPlayer, setStagedPlayer] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isOrderModalVisible, setOrderModalVisible] = useState(false);
  const navigate = useNavigate();
  const { userId } = useAuth();
  const reconnectToastId = useRef(null);

  const handlePlayerSelect = (player) => {
    if (socket && draftState?.currentPicker?.userId === userId) {
      socket.emit('makePick', { 
        contestId: contest._id, 
        player: player,
        userId: userId
      });
      setStagedPlayer(null);
    } else {
      console.log("Not your turn to pick, socket not connected, or no player selected.");
    }
  };

  const handleConfirmDraft = () => {
    if (stagedPlayer) {
      handlePlayerSelect(stagedPlayer);
    }
  };

  const handleWithdraw = () => {
    if (socket && window.confirm('Are you sure you want to withdraw from this draft? This action cannot be undone.')) {
        console.log(`[SnakeDraftModule] Emitting withdrawFromDraft for user ${userId}`);
        socket.emit('withdrawFromDraft', { 
          contestId: contest._id,
          userId: userId
        });
    }
  };

  useEffect(() => {
    console.log('[SnakeDraftModule] Initializing with contest:', contest);
    console.log('[Socket.IO] Attempting to connect to:', SOCKET_URL);
    const newSocket = io(SOCKET_URL, {
      path: '/socket.io',
      transports: ['polling', 'websocket'],
      withCredentials: true,
      forceNew: true,
      reconnectionAttempts: 5,
      timeout: 10000,
      secure: process.env.NODE_ENV === 'production'
    });
    
    newSocket.on('connect', () => {
      console.log('[Socket.IO] Connected to server');
      console.log('[Socket.IO] Joining draft room:', contest._id);
      setIsConnected(true);
      newSocket.emit('joinDraftRoom', { contestId: contest._id });
    });

    newSocket.on('draftStateUpdate', (newState) => {
      console.log('[Socket.IO] Received draft state update:', newState);
      setDraftState(newState);
      
      // Clear selected player when state updates
      setStagedPlayer(null);
    });

    newSocket.on('draftEnded', (data) => {
      console.log('[Socket.IO] Draft has ended:', data.message);
      // The toast notification is now handled by the 'userWithdrew' event.
      // This handler's only job is to navigate the user away.
      
      // Navigate back to the contest lobby after a delay
      setTimeout(() => {
        navigate(`/contests/registering/${contest._id}`);
        newSocket.disconnect();
      }, 3000);
    });

    // Add error handling for failed connections
    newSocket.on('connect_error', (error) => {
      console.error('[Socket.IO] Connection error:', error);
      setIsConnected(false);
      toast.error('Failed to connect to draft server. Please refresh the page.');
    });

    newSocket.on('draftComplete', () => {
      console.log('[Socket.IO] Draft complete');
      toast.success('Draft complete! Redirecting to contest lobby...');
      
      // Navigate to the running contests tab
      setTimeout(() => {
        // First navigate, then cleanup socket
        navigate('/contests/running');
        // Cleanup socket after navigation
        newSocket.disconnect();
      }, 2000);
    });

    newSocket.on('draftError', (error) => {
      console.error('[Socket.IO] Draft error:', error);
      toast.error(error.message);
    });

    // Move userWithdrew event handler to top level of socket events
    newSocket.on('userWithdrew', (data) => {
      console.log('[Socket.IO] Received userWithdrew event:', data);
      const { userId: withdrawnUserId, username } = data;
      
      if (withdrawnUserId === userId) {
        console.log('[SnakeDraftModule] Current user withdrew, navigating to:', `/contests/registering/${contest._id}`);
        toast.success('You have successfully unregistered from this contest.');
        
        // --- NEW: Clear session storage to allow modal to show again ---
        const modalShownKey = `draftOrderModalShown_${contest._id}`;
        sessionStorage.removeItem(modalShownKey);
        console.log(`[SnakeDraftModule] Cleared sessionStorage key: ${modalShownKey}`);
        // --- END NEW ---

        // Navigate back to contest lobby
        navigate(`/contests/registering/${contest._id}`);
        // Cleanup socket
        newSocket.disconnect();
        setSocket(null);
        setIsConnected(false);
      } else {
        console.log('[SnakeDraftModule] Other user withdrew:', username);
        toast(`${username} has unregistered from this contest.`);
        // The 'draftStateUpdate' event will be sent by the server to refresh the state.
        // No need to manually re-join the room.
      }
    });

    newSocket.on('reconnect', () => {
      console.log('[Socket.IO] Reconnected to server');
      if (reconnectToastId.current) {
        toast.success('Reconnected successfully!', { id: reconnectToastId.current });
        reconnectToastId.current = null;
      }
    });

    newSocket.on('reconnect_failed', () => {
      console.error('[Socket.IO] Reconnection failed');
      if (reconnectToastId.current) {
        toast.error('Could not reconnect. Please refresh the page.', { id: reconnectToastId.current, duration: Infinity });
      } else {
        toast.error('Could not reconnect. Please refresh the page.', { duration: Infinity });
      }
    });

    newSocket.on('disconnect', (reason) => {
      console.log(`[Socket.IO] Disconnected. Reason: ${reason}`);
      setIsConnected(false);
      // Only show toast for unexpected disconnects
      if (reason !== 'io client disconnect') {
        reconnectToastId.current = toast.loading('Attempting to reconnect to draft lobby...');
      }
    });

    setSocket(newSocket);

    return () => {
      if (newSocket) {
        console.log('[Socket.IO] Cleaning up socket connection');
        newSocket.disconnect();
      }
    };
  }, [contest._id, navigate, userId]);

  // Handles timeout for receiving initial draft state
  useEffect(() => {
    if (isConnected && !draftState) {
      const timeoutId = setTimeout(() => {
        // Re-check in case draftState arrived just before timeout
        if (!draftState) {
          console.error('[SnakeDraftModule] No draft state received after 5 seconds');
          toast.error('Failed to load draft state. Please refresh the page.');
        }
      }, 5000);

      return () => clearTimeout(timeoutId);
    }
  }, [isConnected, draftState]);

  // --- NEW: Show Draft Order Modal Logic ---
  useEffect(() => {
    if (draftState && draftState.entrants) {
      const modalShownKey = `draftOrderModalShown_${contest._id}`;
      const hasModalBeenShown = sessionStorage.getItem(modalShownKey);

      if (!hasModalBeenShown) {
        setOrderModalVisible(true);
        sessionStorage.setItem(modalShownKey, 'true');
      }
    }
  }, [draftState, contest._id]);
  // --- END NEW ---

  if (!draftState || !userId) {
    return (
      <div className="loading-container">
        Loading Draft Room...
      </div>
    );
  }
  
  const { availablePlayers, entrants, draftPicks, ...status } = draftState;
  const isMyTurn = draftState.currentPicker?.userId === userId;

  return (
    <div className="module-container snake-draft-container">
      <DraftOrderModal
        isOpen={isOrderModalVisible}
        onClose={() => setOrderModalVisible(false)}
        entrants={entrants || []}
      />
      <DraftStatusHeader draftState={status} userId={userId} />

      <div className="snake-draft-main-content">
        <div className="snake-draft-left-panel">
          {!status.isDraftComplete && (
            <div className="draft-confirmation-bar">
              <input 
                type="text" 
                className="confirmation-input"
                value={stagedPlayer ? stagedPlayer.name : ''} 
                placeholder="Select a player from the list"
                readOnly 
              />
              <button 
                className="draft-btn"
                onClick={handleConfirmDraft}
                disabled={!stagedPlayer || !isMyTurn || status.isDraftComplete}
              >
                Draft
              </button>
            </div>
          )}
          <div className="player-picker-wrapper">
            <PlayerPickerSnake 
              players={availablePlayers}
              onStagePlayer={setStagedPlayer}
              stagedPlayer={stagedPlayer}
              disabled={!isMyTurn || status.isDraftComplete}
              sport={contest.sport}
            />
          </div>
        </div>
        
        <div className="snake-draft-right-panel">
          <DraftBoard 
            draftPicks={draftPicks} 
            entrants={entrants} 
            contestFormat={contest.format} 
            sport={contest.sport}
          />
          <div className="draft-info-banner">
            <span className="info-text">Note: Your first drafted player will be assigned as your team captain (CPT)</span>
          </div>
          <EntrantRosters entrants={entrants} myUserId={userId} sport={contest.sport} />
          {!status.isDraftComplete && (
            <div className="withdraw-link-container">
              <button onClick={() => setOrderModalVisible(true)} className="withdraw-link">
                View Draft Order
              </button>
              <button onClick={handleWithdraw} className="withdraw-link">
                Withdraw from Draft
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

SnakeDraftModule.propTypes = {
  contest: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    players: PropTypes.array,
    format: PropTypes.string.isRequired, // Ensure format is passed
    sport: PropTypes.string.isRequired,
  }).isRequired,
};

export default SnakeDraftModule; 